/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Grid, Box, Typography, MenuItem, Select, FormControl } from '@mui/material';
import {
    MonetizationOnOutlined,
    ShoppingBagOutlined,
    AccountBalanceWalletOutlined,
    ReportProblemOutlined,
    ShoppingCartOutlined,
    StorefrontOutlined,
    TwoWheelerOutlined,
    AssignmentIndOutlined,
    FactCheckOutlined
} from '@mui/icons-material';

// Components
import DashboardStatCard from '../../vendor/dashboard/DashboardStatCard'; // Reused
import VendorApplicationList from './VendorApplicationList'; // New
import AdminRevenueChart from './AdminRevenueChart';
import AdminOrderStatusChart from './AdminOrderStatusChart';
import TopVendorsChart from './TopVendorsChart';
import {
    useAdminStatsQuery,
    useAdminChartsQuery,
    useAdminRecentQuery,
    useAdminTopVendorsQuery,
    useAdminVendorApplicationsQuery
} from 'hooks/useAdminDashboard';

import RecentOrdersList from '../../vendor/dashboard/RecentOrdersList'; // Reused, assuming it can fetch all orders

export default function AdminDashboard() {
    const [timeRange, setTimeRange] = useState('monthly');

    // Queries
    const { data: dashboardStats, isLoading: loadingStats } = useAdminStatsQuery();
    const { data: dashboardCharts, isLoading: loadingCharts } = useAdminChartsQuery(timeRange);
    const { data: recentOrders, isLoading: loadingRecent } = useAdminRecentQuery();
    const { data: topVendors, isLoading: loadingTopVendors } = useAdminTopVendorsQuery();
    const { data: applications, isLoading: loadingApps } = useAdminVendorApplicationsQuery();

    // Fallback to recommended data if API returns null/undefined (optional, can be removed if API is reliable)
    const stats = dashboardStats?.data;
    // Chart Data
    const revenueData = dashboardCharts?.data?.revenue;
    const orderStatusRaw = dashboardCharts?.data?.orderStatus || {};
    const orderStatusData = [
        { name: 'Active', value: orderStatusRaw.active || 0, color: '#f6c23e' },
        { name: 'Completed', value: orderStatusRaw.completed || 0, color: '#1cc88a' },
        { name: 'Cancelled', value: orderStatusRaw.cancelled || 0, color: '#e74a3b' }
    ];
    const topVendorsData = topVendors?.data;

    // Recent Orders
    const allRecentOrders = recentOrders?.data;

    // Applications
    const allApplications = applications?.data;

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            {/* HEADER & TIME FILTER */}
            <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" fontWeight={700}>
                    Admin Dashboard
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150, background: 'white', borderRadius: 1 }}>
                    <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                        <MenuItem value="daily">Daily View</MenuItem>
                        <MenuItem value="weekly">Weekly View</MenuItem>
                        <MenuItem value="monthly">Monthly View</MenuItem>
                        <MenuItem value="yearly">Yearly View</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* 1. PLATFORM HEALTH */}
            <Grid container spacing={3} mb={4}>
                {/* 1. PENDING APPLICATIONS - HIGH PRIORITY */}
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardStatCard
                        title="Vendor Applications"
                        value={stats?.pendingApplications || '0'}
                        color="error" // Red to grab attention
                        icon={<AssignmentIndOutlined fontSize="large" />}
                        isLoading={loadingStats}
                        to="/admin/vendors" // Could filter by pending in the future
                    />
                </Grid>

                {/* 2. PENDING KYC - HIGH PRIORITY */}
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardStatCard
                        title="Pending KYC"
                        value={stats?.pendingKyc || '0'}
                        color="warning" // Yellow/Orange
                        icon={<FactCheckOutlined fontSize="large" />}
                        isLoading={loadingStats}
                        to="/admin/kyc"
                    />
                </Grid>

                {/* 3. PENDING PAYOUTS */}
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardStatCard
                        title="Pending Payouts"
                        value={stats?.pendingPayouts || '0'}
                        color="warning"
                        icon={<AccountBalanceWalletOutlined fontSize="large" />}
                        isLoading={loadingStats}
                        to="/admin/payouts"
                    />
                </Grid>

                {/* 4. TOTAL REVENUE */}
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardStatCard
                        title="Total Revenue"
                        value={`₦${stats?.revenue?.toLocaleString() || '0'}`}
                        color="success"
                        icon={<MonetizationOnOutlined fontSize="large" />}
                        isLoading={loadingStats}
                        to="/admin/transactions"
                    />
                </Grid>

                {/* 5. ACTIVE VENDORS */}
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardStatCard
                        title="Active Vendors"
                        value={stats?.vendors || '0'}
                        color="primary"
                        icon={<StorefrontOutlined fontSize="large" />}
                        isLoading={loadingStats}
                        to="/admin/vendors"
                    />
                </Grid>

                {/* 6. ACTIVE RIDERS */}
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardStatCard
                        title="Active Riders"
                        value={stats?.riders || '0'}
                        color="primary"
                        icon={<TwoWheelerOutlined fontSize="large" />}
                        isLoading={loadingStats}
                        to="/admin/riders"
                    />
                </Grid>

                {/* 7. TOTAL ORDERS */}
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardStatCard
                        title="Total Orders"
                        value={stats?.orders || '0'}
                        color="info"
                        icon={<ShoppingCartOutlined fontSize="large" />}
                        isLoading={loadingStats}
                        to="/admin/orders"
                    />
                </Grid>

                {/* 8. PENDING COMPLAINTS */}
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardStatCard
                        title="Pending Complaints"
                        value={stats?.pendingComplaints || '0'}
                        color="error"
                        icon={<ReportProblemOutlined fontSize="large" />}
                        isLoading={loadingStats}
                        to="/admin/complaints"
                    />
                </Grid>
            </Grid>

            {/* 2. CHARTS SECTION */}
            <Grid container spacing={3} mb={4}>
                {/* Revenue Trend (Line Chart) */}
                <Grid item xs={12} lg={8}>
                    <AdminRevenueChart data={revenueData} isLoading={loadingCharts} />
                </Grid>
                {/* Order Status (Pie Chart) */}
                <Grid item xs={12} lg={4}>
                    <AdminOrderStatusChart data={orderStatusData} isLoading={loadingCharts} />
                </Grid>
            </Grid>

            {/* 3. SECONDARY CHARTS & LISTS */}
            <Grid container spacing={3}>
                {/* Top Vendors (Bar Chart) */}
                <Grid item xs={12} md={6} lg={4}>
                    <TopVendorsChart data={topVendorsData} isLoading={loadingTopVendors} />
                </Grid>

                {/* Application List (Existing) */}
                <Grid item xs={12} md={6} lg={4}>
                    <VendorApplicationList applications={applications?.data || []} isLoading={loadingApps} />
                </Grid>

                {/* Recent Orders (Existing Table) */}
                <Grid item xs={12} md={12} lg={4}>
                    <RecentOrdersList orders={allRecentOrders} isLoading={loadingRecent} />
                </Grid>
            </Grid>
        </Box>
    );
}
